import { isDefined, isNotDefined } from '@sgme/fp';
import clsx from 'clsx';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { getSeparatorsFromLocale } from '@/components/sg-design-system/utils/number.ts';
import type { BidAskPoint } from '@/utils/number-point.ts';
import { getTheme } from '@/utils/theme.ts';

//  NORMAL
// ┌─────┐┌─────┐┌─────┐╔═════╗╔═════╗╔═════╗╔═════╗╔═════╗╔═════╗
// │     ││     ││     │║     ║║     ║║     ║║     ║║     ║║     ║
// │  -  ││  3  ││  0  │║  7  ║║  .  ║║  9  ║║  2  ║║  5  ║║  0  ║
// │     ││     ││     │║     ║║     ║║     ║║     ║║     ║║     ║
// └─────┘└─────┘└─────┘╚═════╝╚═════╝╚═════╝╚═════╝╚═════╝╚═════╝
//                         ▲
//                         │
//                         │
//
//                 hightlight index   major index
//
//                         │               │
//  ZOOM                   │               ▼
//  majorDigitPosition=-4  ▼          ╔═════════╗╔═════════╗╔═══╗╔═══╗
// ┌─────┐┌─────┐┌─────┐╔═════╗╔═════╗║         ║║         ║║ 5 ║║ 0 ║
// │     ││     ││     │║     ║║     ║║         ║║         ║╚═══╝╚═══╝
// │  -  ││  3  ││  0  │║  7  ║║  .  ║║    9    ║║    2    ║
// │     ││     ││     │║     ║║     ║║         ║║         ║
// └─────┘└─────┘└─────┘╚═════╝╚═════╝║         ║║         ║
//                                    ╚═════════╝╚═════════╝
//  ◀ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ─ ▶ ◀ ─ ─ ─ ─ ─ ─ ─ ─ ─ ▶  ◀ ─ ─ ─▶
//
//              MINOR DIGITS               MAJOR DIGITS      EXPONENTS

// HIGHTLIGHT / MAJOR DIGIT POSITION
//
//                                   -1
//                                ◀───────
//                      ┌─────┐╔═════╗╔═════╗╔═════╗╔═════╗
//                      │     │║     ║║     ║║     ║║     ║
// digit position = -1  │  3  │║  7  ║║  .  ║║  9  ║║  2  ║
//                      │     │║     ║║     ║║     ║║     ║
//                      └─────┘╚═════╝╚═════╝╚═════╝╚═════╝
//
//                      ┌─────┐┌─────┐┌─────┐╔═════╗╔═════╗
//                      │     ││     ││     │║     ║║     ║
// digit position = 0   │  3  ││  7  ││  .  │║  9  ║║  2  ║
//                      │     ││     ││     │║     ║║     ║
//                      └─────┘└─────┘└─────┘╚═════╝╚═════╝
//
//                      ┌─────┐┌─────┐┌─────┐┌─────┐╔═════╗
//                      │     ││     ││     ││     │║     ║
// digit position = 1   │  3  ││  7  ││  .  ││  9  │║  2  ║
//                      │     ││     ││     ││     │║     ║
//                      └─────┘└─────┘└─────┘└─────┘╚═════╝
//                                       ──────────────▶
//                                           1

type FormattedPriceProps = {
  value: number;

  // the number of visible digits after the dot
  precision: number;

  /**
   * if defined => ZOOM MODE = number of digits considered as EXPONENTS with 2 MAJOR digits
   * if not defined => NORMAL MODE
   */
  exponentCount?: number;

  /**
   * if defined => highlight some digits
   */
  highlightDigitPosition?: number;
  highlightColor?: string;

  plusPrefixHidden?: boolean;

  size?: 'sm' | 'md' | 'lg';

  'data-e2e'?: string;

  'data-e2e-rate-type'?: 'executable' | 'indicative';
  'data-e2e-can-trade'?: boolean;

  showColoredPrices?: boolean;
};

export const FormattedPrice = (props: FormattedPriceProps) => {
  const {
    value,
    precision,
    exponentCount,
    highlightDigitPosition,
    highlightColor = DEFAULT_HIGHLIGHTED_DIGIT_COLOR,
    plusPrefixHidden: isPlusHidden,
    size = 'md',
    'data-e2e': dataE2e,
    'data-e2e-rate-type': dataE2eRateType,
    'data-e2e-can-trade': dataE2eCanTrade,
    showColoredPrices,
  } = props;

  const theme = getTheme();
  const chosenHighlightColor = showColoredPrices
    ? theme === 'standard'
      ? DEFAULT_COLORED_WHITE_HIGHLIGHTED_DIGIT_COLOR
      : DEFAULT_COLORED_BLACK_HIGHLIGHTED_DIGIT_COLOR
    : highlightColor;

  const containerRef = useRef<HTMLDivElement>(null);

  const { locale } = useIntl();
  // oxlint-disable-next-line exhaustive-deps -- locale changes
  const decimalSeparator = useMemo(() => getSeparatorsFromLocale(locale).decimalSeparator, [locale]);

  const [textScale, setTextScale] = useState(1);

  useEffect(() => {
    if (isNotDefined(containerRef.current)) {
      return;
    }

    const parentNode = containerRef.current.parentElement;

    if (isNotDefined(parentNode)) {
      return;
    }

    const parentWidth = parentNode.getBoundingClientRect().width;
    const containerWidth = containerRef.current.scrollWidth;

    if (containerWidth > parentWidth) {
      setTextScale(parentWidth / containerWidth);
    }
  }, []);

  // oxlint-disable-next-line exhaustive-deps -- locale changes
  const allDigitsWithPrefix = useMemo(() => {
    const allDigits = formatPriceDigits(value, precision, locale);
    return !isPlusHidden && value >= 0 ? ['+', ...allDigits] : allDigits;
  }, [value, precision, isPlusHidden, locale]);

  const dotIndex = allDigitsWithPrefix.indexOf(decimalSeparator);

  const highlightIndex = useMemo(() => {
    const dotIndex = allDigitsWithPrefix.indexOf(decimalSeparator);

    return getDigitIndexFromPosition(highlightDigitPosition, dotIndex);
  }, [allDigitsWithPrefix, highlightDigitPosition, decimalSeparator]);

  return (
    <div
      className="d-flex align-items-baseline lh-1 monospace-numbers"
      data-e2e={dataE2e}
      data-e2e-rate-type={dataE2eRateType}
      data-e2e-can-trade={dataE2eCanTrade}
      ref={containerRef}
      style={{
        transformOrigin: 'left center',
        transform: `scale(${textScale})`,
        // force all digits to be monospaced (same width)
        fontFeatureSettings: '"cv01", "cv10", "tnum"',
      }}
    >
      {allDigitsWithPrefix.map((digit, digitIndex) => (
        <span
          className={getDigitClassName(digitIndex, dotIndex, allDigitsWithPrefix.length, exponentCount, size)}
          style={getDigitStyle(digitIndex, highlightIndex, chosenHighlightColor)}
          data-e2e={
            isDefined(dataE2e) && checkIfHighlighted(digitIndex, highlightIndex) ? `${dataE2e}-highlight` : undefined
          }
          // oxlint-disable-next-line no-array-index-key -- the index is really the key
          key={digitIndex}
        >
          {digit}
        </span>
      ))}
    </div>
  );
};

const formatPriceDigits = (value: number, precision: number, locale: string): string[] =>
  value.toLocaleString(locale, { minimumFractionDigits: precision, maximumFractionDigits: precision }).split('');

const getDigitIndexFromPosition = (position: number | undefined, dotIndex: number) =>
  isDefined(position) ? (position < 0 ? dotIndex + position : dotIndex + 1 + position) : undefined;

/**
 * value = 1.2345
 * | exponentCount | digitIndex | dotIndex | digitType  |
 * |---------------|------------|----------|------------|
 * | undefined     | 0          | 1        | MAJOR      |
 * | undefined     | 1          | 1        | MAJOR      |
 * | undefined     | 2          | 1        | MAJOR      |
 * | undefined     | 3          | 1        | MAJOR      |
 * | undefined     | 4          | 1        | MAJOR      |
 * | undefined     | 5          | 1        | MAJOR      |
 *
 * value = 1.2345
 * | exponentCount | digitIndex | dotIndex | digitType  |
 * |---------------|------------|----------|------------|
 * | 2             | 0          | 1        | minor      |
 * | 2             | 1          | 1        | minor      |
 * | 2             | 2          | 1        | MAJOR      |
 * | 2             | 3          | 1        | MAJOR      |
 * | 2             | 4          | 1        | Exponent   |
 * | 2             | 5          | 1        | Exponent   |
 *
 * value = 1.234
 * | exponentCount | digitIndex | dotIndex | digitType  |
 * |---------------|------------|----------|------------|
 * | 2             | 0          | 1        | minor      |
 * | 2             | 1          | 1        | minor      |
 * | 2             | 2          | 1        | MAJOR      |
 * | 2             | 3          | 1        | MAJOR      |
 * | 2             | 4          | 1        | Exponent   |
 *
 * value = 1.23
 * | exponentCount | digitIndex | dotIndex | digitType  |
 * |---------------|------------|----------|------------|
 * | 2             | 0          | 1        | minor      |
 * | 2             | 1          | 1        | minor      |
 * | 2             | 2          | 1        | MAJOR      |
 * | 2             | 3          | 1        | MAJOR      |
 *
 * value = 1.2
 * | exponentCount | digitIndex | dotIndex | digitType  |
 * |---------------|------------|----------|------------|
 * | 2             | 0          | 1        | minor      |
 * | 2             | 1          | 1        | minor      |
 * | 2             | 2          | 1        | MAJOR      |
 */
const getDigitClassName = (
  digitIndex: number,
  dotIndex: number,
  digitCount: number,
  exponentCount: number | undefined,
  size: 'sm' | 'md' | 'lg',
) => {
  const visibleExponentCount = isDefined(exponentCount)
    ? digitCount - dotIndex > 4 && exponentCount === 2
      ? 2
      : digitCount - dotIndex > 3
        ? 1
        : 0
    : undefined;

  const majorIndex = isDefined(visibleExponentCount) ? digitCount - 2 - visibleExponentCount : undefined;
  const exponentIndex = isDefined(visibleExponentCount) ? digitCount - visibleExponentCount : undefined;

  const digitType =
    isNotDefined(majorIndex) || isNotDefined(exponentIndex)
      ? 'MAJOR'
      : digitIndex <= dotIndex || digitIndex < majorIndex
        ? 'MINOR'
        : digitIndex >= majorIndex && digitIndex < exponentIndex
          ? 'MAJOR'
          : 'EXPONENT';

  return clsx(['text-nowrap', DIGIT_FONT_SIZE[digitType][size]], {
    'align-self-start mt-1': digitType === 'EXPONENT',
  });
};

const getDigitStyle = (digitIndex: number, highlightIndex: number | undefined, highlightColor: string) => {
  return checkIfHighlighted(digitIndex, highlightIndex)
    ? {
        color: highlightColor,
      }
    : undefined;
};

const checkIfHighlighted = (digitIndex: number, highlightIndex: number | undefined) => {
  return isDefined(highlightIndex) && digitIndex >= highlightIndex;
};

// warning: text-warning is darker than the old FX
const DEFAULT_HIGHLIGHTED_DIGIT_COLOR = 'rgb(249, 141, 0)';
const DEFAULT_COLORED_WHITE_HIGHLIGHTED_DIGIT_COLOR = 'rgb(0, 0, 0)';
const DEFAULT_COLORED_BLACK_HIGHLIGHTED_DIGIT_COLOR = 'rgb(255, 255, 255)';

const DIGIT_FONT_SIZE = {
  MINOR: {
    sm: 'fs-14 lh-1',
    md: 'fs-18 lh-1',
    lg: 'fs-21 lh-1',
  },
  MAJOR: {
    sm: 'fs-30 lh-1',
    md: 'fs-36 lh-1',
    lg: 'fs-48 lh-1',
  },
  EXPONENT: {
    sm: 'fs-12 lh-1',
    md: 'fs-16 lh-1',
    lg: 'fs-18 lh-1',
  },
};

// commercialMarginSpotPoints
// - =   0 => undefined
// - <   1 => precision
// - <  10 => precision - 1
// - < 100 => precision - 2
// ...
export const getHighlightDigitPosition = (precision: number, margin: BidAskPoint) => {
  return {
    bid: margin.bid === 0 ? undefined : precision - Math.floor(Math.log10(Math.abs(margin.bid))) - 1,
    ask: margin.ask === 0 ? undefined : precision - Math.floor(Math.log10(Math.abs(margin.ask))) - 1,
  };
};
